import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faSearch, faExclamationTriangle, faCheckDouble, faSquareCheck, faEye } from '@fortawesome/free-solid-svg-icons';

/**
 * SpanSelectorTable Component
 * 
 * Table-based element selection for repair projects
 * - Compact table layout showing more elements
 * - Includes inspection data columns
 * - Sortable columns
 * - Bulk selection options
 * - Visual indicators for inspection data availability
 */
const SpanSelectorTable = ({ bridgeId, onSelectionChange, maxSpans = null, levelLabel = 'Elements' }) => {
  const [availableSpans, setAvailableSpans] = useState([]);
  const [filteredSpans, setFilteredSpans] = useState([]);
  const [selectedSpanIds, setSelectedSpanIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('span_name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [directionFilter, setDirectionFilter] = useState('');
  const [corrosionFilter, setCorrosionFilter] = useState('');
  const [inspectionFilter, setInspectionFilter] = useState('');

  useEffect(() => {
    if (bridgeId) {
      fetchAvailableSpans();
    }
  }, [bridgeId]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, directionFilter, corrosionFilter, inspectionFilter, availableSpans]);

  const fetchAvailableSpans = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/repair-projects/available-spans?bridge_id=${bridgeId}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setAvailableSpans(data.data);
        setError(null);
      } else {
        setError(data.message || `Failed to fetch ${levelLabel.toLowerCase()}`);
      }
    } catch (err) {
      console.error(`Error fetching ${levelLabel.toLowerCase()}:`, err);
      setError(`Failed to fetch ${levelLabel.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...availableSpans];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(span => 
        span.span_code?.toLowerCase().includes(term) ||
        span.span_name?.toLowerCase().includes(term) ||
        span.frame_name?.toLowerCase().includes(term) ||
        span.direction?.toLowerCase().includes(term) ||
        span.corrosion_category?.toLowerCase().includes(term) ||
        span.damage_severity?.toLowerCase().includes(term)
      );
    }

    // Direction filter
    if (directionFilter) {
      filtered = filtered.filter(span => span.direction === directionFilter);
    }

    // Corrosion filter
    if (corrosionFilter) {
      filtered = filtered.filter(span => span.corrosion_category === corrosionFilter);
    }

    // Inspection data filter
    if (inspectionFilter) {
      if (inspectionFilter === 'has_inspection') {
        filtered = filtered.filter(span => span.has_inspection_data === true);
      } else if (inspectionFilter === 'no_inspection') {
        filtered = filtered.filter(span => span.has_inspection_data === false);
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle numeric fields
      if (sortField === 'total_area') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setFilteredSpans(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSpanToggle = (spanId) => {
    let newSelection;
    if (selectedSpanIds.includes(spanId)) {
      newSelection = selectedSpanIds.filter(id => id !== spanId);
    } else {
      if (maxSpans && selectedSpanIds.length >= maxSpans) {
        alert(`Maximum ${maxSpans} ${levelLabel.toLowerCase()} can be selected for a repair project`);
        return;
      }
      newSelection = [...selectedSpanIds, spanId];
    }
    setSelectedSpanIds(newSelection);
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  const selectAllSpans = () => {
    const allSpanIds = availableSpans.map(span => span.id);
    setSelectedSpanIds(allSpanIds);
    onSelectionChange(allSpanIds, availableSpans);
  };

  const deselectAllSpans = () => {
    setSelectedSpanIds([]);
    onSelectionChange([], []);
  };

  const selectFilteredSpans = () => {
    const filteredSpanIds = filteredSpans.map(span => span.id);
    const newSelection = [...new Set([...selectedSpanIds, ...filteredSpanIds])];
    setSelectedSpanIds(newSelection);
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  const deselectFilteredSpans = () => {
    const filteredSpanIds = filteredSpans.map(span => span.id);
    const newSelection = selectedSpanIds.filter(id => !filteredSpanIds.includes(id));
    setSelectedSpanIds(newSelection);
    const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
    onSelectionChange(newSelection, selectedSpans);
  };

  const isSelected = (spanId) => selectedSpanIds.includes(spanId);
  const canSelect = !maxSpans || selectedSpanIds.length < maxSpans;

  const getUniqueDirections = () => {
    return [...new Set(availableSpans.map(span => span.direction).filter(Boolean))];
  };

  const getUniqueCorrosionCategories = () => {
    return [...new Set(availableSpans.map(span => span.corrosion_category).filter(Boolean))];
  };

  const getCorrosionBadgeClass = (category) => {
    switch (category) {
      case 'C5': return 'bg-red-100 text-red-800';
      case 'C3': return 'bg-yellow-100 text-yellow-800';
      case 'C2': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDamageBadgeClass = (severity) => {
    switch (severity) {
      case 'C': return 'bg-red-100 text-red-800';
      case 'B': return 'bg-yellow-100 text-yellow-800';
      case 'A': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredSpans.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSpans = filteredSpans.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading {levelLabel.toLowerCase()}...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Selection Status */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-700 mt-1">
          {selectedSpanIds.length} {levelLabel.toLowerCase()}{selectedSpanIds.length !== 1 ? '' : ''} selected
          {maxSpans && ` (max ${maxSpans})`}
        </p>
        {maxSpans && selectedSpanIds.length === maxSpans && (
          <div className="flex items-center text-yellow-700 bg-yellow-100 px-3 py-2 rounded mt-2">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            <span className="text-sm font-medium">Maximum reached</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FontAwesomeIcon icon={faSearch} className="mr-2" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`${levelLabel} name, frame...`}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="mt-1 text-xs text-gray-500">
              Search by: {levelLabel.toLowerCase()} name, frame, direction, corrosion, damage
            </div>
          </div>

          {/* Direction Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Direction</label>
            <select
              value={directionFilter}
              onChange={(e) => setDirectionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Directions</option>
              {getUniqueDirections().map(dir => (
                <option key={dir} value={dir}>{dir}</option>
              ))}
            </select>
          </div>

          {/* Corrosion Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Corrosion Category</label>
            <select
              value={corrosionFilter}
              onChange={(e) => setCorrosionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {getUniqueCorrosionCategories().map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Inspection Data Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspection Data</label>
            <select
              value={inspectionFilter}
              onChange={(e) => setInspectionFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All {levelLabel}</option>
              <option value="has_inspection">Has Inspection Data</option>
              <option value="no_inspection">No Inspection Data</option>
            </select>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-2">
            {/* Bulk Selection Buttons */}
            {availableSpans.length > 0 && (
              <>
                <button
                  onClick={selectAllSpans}
                  className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  title={`Select all ${levelLabel.toLowerCase()}`}
                >
                  <FontAwesomeIcon icon={faCheckDouble} className="mr-1" />
                  Select All
                </button>
                <button
                  onClick={deselectAllSpans}
                  className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
                  title={`Deselect all ${levelLabel.toLowerCase()}`}
                >
                  <FontAwesomeIcon icon={faSquare} className="mr-1" />
                  Deselect All
                </button>
                {filteredSpans.length > 0 && filteredSpans.length < availableSpans.length && (
                  <>
                    <button
                      onClick={selectFilteredSpans}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                      title="Select all filtered spans"
                    >
                      <FontAwesomeIcon icon={faSquareCheck} className="mr-1" />
                      Select Filtered
                    </button>
                    <button
                      onClick={deselectFilteredSpans}
                      className="px-3 py-1 text-sm bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 transition-colors"
                      title="Deselect all filtered spans"
                    >
                      <FontAwesomeIcon icon={faSquare} className="mr-1" />
                      Deselect Filtered
                    </button>
                  </>
                )}
              </>
            )}
          </div>
          
          {(searchTerm || directionFilter || corrosionFilter || inspectionFilter) && (
            <button
              onClick={() => {
                setSearchTerm('');
                setDirectionFilter('');
                setCorrosionFilter('');
                setInspectionFilter('');
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear All Filters
            </button>
          )}
        </div>
      </div>

      {/* Spans Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredSpans.length)} of {filteredSpans.length} {levelLabel.toLowerCase()}{filteredSpans.length !== 1 ? '' : ''}
              {availableSpans.length > 0 && (
                <span className="text-gray-500"> (from {availableSpans.length} total)</span>
              )}
            </p>
            {totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
              </div>
            )}
          </div>
        </div>
        
        {availableSpans.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-700 mb-2">No {levelLabel.toLowerCase()} available</p>
            <p className="text-sm text-gray-500">
              This bridge may not have any {levelLabel.toLowerCase()} with valid area data, or {levelLabel.toLowerCase()} may not be active.
            </p>
          </div>
        ) : filteredSpans.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="mb-4">
              <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-400" />
            </div>
            <p className="text-lg font-medium text-gray-700 mb-2">No {levelLabel.toLowerCase()} found matching your criteria</p>
            <p className="text-sm text-gray-500 mb-4">
              Try adjusting your search terms or filters to see more results.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setDirectionFilter('');
                setCorrosionFilter('');
                setInspectionFilter('');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Clear All Filters
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto max-h-96"> {/* Fixed height with scroll */}
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={currentSpans.length > 0 && currentSpans.every(span => selectedSpanIds.includes(span.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const currentSpanIds = currentSpans.map(span => span.id);
                            const newSelection = [...new Set([...selectedSpanIds, ...currentSpanIds])];
                            setSelectedSpanIds(newSelection);
                            const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
                            onSelectionChange(newSelection, selectedSpans);
                          } else {
                            const currentSpanIds = currentSpans.map(span => span.id);
                            const newSelection = selectedSpanIds.filter(id => !currentSpanIds.includes(id));
                            setSelectedSpanIds(newSelection);
                            const selectedSpans = availableSpans.filter(span => newSelection.includes(span.id));
                            onSelectionChange(newSelection, selectedSpans);
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('span_name')}
                    >
                      {levelLabel} Name
                      {sortField === 'span_name' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Frame
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Direction
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('total_area')}
                    >
                      Area (m²)
                      {sortField === 'total_area' && (
                        <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Corrosion
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Damage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coating
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inspection Data
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentSpans.map(span => (
                    <tr
                      key={span.id}
                      onClick={() => (canSelect || isSelected(span.id)) && handleSpanToggle(span.id)}
                      className={`
                        cursor-pointer transition-colors
                        ${isSelected(span.id)
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : canSelect
                            ? 'hover:bg-gray-50'
                            : 'opacity-50 cursor-not-allowed'
                        }
                      `}
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <FontAwesomeIcon
                          icon={isSelected(span.id) ? faCheckSquare : faSquare}
                          className={`text-lg ${isSelected(span.id) ? 'text-blue-600' : 'text-gray-400'}`}
                        />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {span.span_name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {span.frame_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {span.direction || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {span.total_area ? parseFloat(span.total_area).toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {span.corrosion_category ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getCorrosionBadgeClass(span.corrosion_category)}`}>
                            {span.corrosion_category}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {span.damage_severity ? (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getDamageBadgeClass(span.damage_severity)}`}>
                            {span.damage_severity}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {span.coating_system || 'N/A'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {span.has_inspection_data ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            Available
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Not Available
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {isSelected(span.id) ? (
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                            Selected
                          </span>
                        ) : (
                          <span className="text-gray-400 text-xs">Not Selected</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={goToPrevPage}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => goToPage(pageNum)}
                            className={`px-3 py-1 text-sm rounded-md ${
                              currentPage === pageNum
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    Showing {startIndex + 1}-{Math.min(endIndex, filteredSpans.length)} of {filteredSpans.length}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Selected Elements Summary */}
      {selectedSpanIds.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Selected {levelLabel} Summary</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {availableSpans
              .filter(span => selectedSpanIds.includes(span.id))
              .map((span, index) => (
                <div key={span.id} className="text-sm text-gray-700">
                  {index + 1}. {span.span_name} ({parseFloat(span.total_area || 0).toLocaleString()}m²)
                </div>
              ))}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-sm text-gray-600">
              <strong>Total Area:</strong> {availableSpans
                .filter(span => selectedSpanIds.includes(span.id))
                .reduce((sum, span) => sum + (parseFloat(span.total_area) || 0), 0)
                .toLocaleString()}m²
            </div>
            <div className="text-sm text-gray-600">
              <strong>{levelLabel} with Inspection Data:</strong> {availableSpans
                .filter(span => selectedSpanIds.includes(span.id) && span.has_inspection_data)
                .length} of {selectedSpanIds.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpanSelectorTable;
